/**
 * Entrada al backoffice (ADM-01).
 *
 * Es una vista aparte, sin la barra lateral del panel: quien no entro todavia
 * no tiene un panel del que ver el armazon. Tampoco lleva la cabecera de la
 * tienda — el backoffice no es una pagina mas del sitio.
 *
 * No se indexa y no dice nunca si un correo existe o no: el error es siempre el
 * mismo, que es lo que impide averiguar quienes son los administradores
 * probando correos.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Icono } from '../../componentes/comunes/Icono';
import { Imagen } from '../../componentes/comunes/Imagen';
import { SITIO } from '../../configuracion/sitio';
import { useSesion } from '../../contexto/sesion-contexto';
import { Seo } from '../../seo/Seo';
import { ErrorDeApi } from '../../servicios/cliente-api';
import { rutas } from '../../utilidades/rutas';

export function Acceso() {
  const { estado, administrador, entrar } = useSesion();
  const navegar = useNavigate();
  // Aviso que deja `RestablecerClave` al volver aca con la clave ya cambiada.
  const aviso = (useLocation().state as { aviso?: string } | null)?.aviso ?? null;

  const [email, setEmail] = useState('');
  const [clave, setClave] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Ya hay sesion: no tiene sentido pedir la clave de nuevo.
  if (estado === 'dentro' && administrador !== null) {
    return (
      <Navigate
        to={administrador.debeCambiarClave ? rutas.administracion.clave() : rutas.administracion.panel()}
        replace
      />
    );
  }

  async function enviar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      await entrar({ email, clave });
      navegar(rutas.administracion.panel(), { replace: true });
    } catch (fallo) {
      setError(
        fallo instanceof ErrorDeApi
          ? fallo.message
          : 'No pudimos conectarnos con el servidor. Intenta de nuevo.',
      );
      setEnviando(false);
    }
  }

  return (
    <>
      <Seo
        titulo="Acceso al panel"
        descripcion="Entrada al panel de administración de El Carpintero."
        ruta={rutas.administracion.acceso()}
        sinIndexar
      />

      <main className="acceso">
        <div className="acceso-caja">
          <div className="acceso-marca">
            <Imagen src={SITIO.logo} alt={SITIO.nombreLargo} ancho={136} alto={68} prioridad />
          </div>

          <h1>Panel de administración</h1>
          <p className="acceso-bajada">Entra con la cuenta que te dieron para administrar la tienda.</p>

          <form
            onSubmit={(evento) => {
              void enviar(evento);
            }}
            noValidate
          >
            {aviso !== null && error === null ? (
              <p className="acceso-aviso" role="status">
                <Icono nombre="checkCirc" />
                <span>{aviso}</span>
              </p>
            ) : null}

            {error !== null ? (
              <p className="acceso-error" role="alert">
                <Icono nombre="alerta" />
                <span>{error}</span>
              </p>
            ) : null}

            <div className="campo">
              <label htmlFor="acceso-email">Correo</label>
              <input
                id="acceso-email"
                name="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(evento) => {
                  setEmail(evento.target.value);
                }}
              />
            </div>

            <div className="campo">
              <label htmlFor="acceso-clave">Clave</label>
              <div className="campo-con-boton">
                <input
                  id="acceso-clave"
                  name="clave"
                  type={verClave ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={clave}
                  onChange={(evento) => {
                    setClave(evento.target.value);
                  }}
                />
                <button
                  type="button"
                  className="campo-boton"
                  onClick={() => {
                    setVerClave((visible) => !visible);
                  }}
                  aria-label={verClave ? 'Ocultar la clave' : 'Mostrar la clave'}
                >
                  <Icono nombre={verClave ? 'ojoNo' : 'ojo'} />
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primario btn-lg btn-bloque" disabled={enviando}>
              {enviando ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          <p className="acceso-enlace">
            <Link to={rutas.administracion.recuperarClave()}>Olvidé mi clave</Link>
          </p>

          <p className="acceso-nota">
            Si no tienes cuenta, pídesela a quien ya administra el panel: las cuentas se crean
            desde adentro y la clave llega por correo.
          </p>
        </div>
      </main>
    </>
  );
}
